import { EllipsisVertical } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

export interface ActionMenuGroup {
  readonly label: string;
  readonly tone?: 'default' | 'danger';
  readonly children: ReactNode;
}

const actionMenuOpenEvent = 'hotle-action-menu-open';

export function ActionMenu({ groups, label = 'Acciones' }: { readonly groups: readonly ActionMenuGroup[]; readonly label?: string }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      const menuWidth = menuRef.current?.offsetWidth ?? 224;
      const menuHeight = Math.min(menuRef.current?.scrollHeight ?? 320, window.innerHeight - 16);
      const belowTop = rect.bottom + 4;
      const aboveTop = rect.top - menuHeight - 4;
      setPosition({
        top: belowTop + menuHeight > window.innerHeight - 8 ? Math.max(8, aboveTop) : belowTop,
        left: Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8)),
      });
    }
    const close = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener('click', close);
    const closeForOtherMenu = () => setOpen(false);
    window.addEventListener(actionMenuOpenEvent, closeForOtherMenu);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener(actionMenuOpenEvent, closeForOtherMenu);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative inline-flex">
      <button
        ref={buttonRef}
        type="button"
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[#d6ded0] bg-white text-[#334033] hover:bg-[#eef3ea]"
        aria-label={label}
        aria-expanded={open}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((current) => {
            if (!current) window.dispatchEvent(new Event(actionMenuOpenEvent));
            return !current;
          });
        }}
      >
        <EllipsisVertical className="h-4 w-4" aria-hidden="true" />
      </button>
      {open ? (
        <div ref={menuRef} className="fixed z-50 max-h-[calc(100vh-1rem)] w-56 overflow-y-auto rounded-md border border-[#d6ded0] bg-white p-2 text-sm shadow-lg" style={{ top: position.top, left: position.left }}>
          {groups.map((group) => (
            <div key={group.label} className="border-t border-[#eef3ea] py-2 first:border-t-0 first:pt-0 last:pb-0">
              <p className={`mb-1 px-2 text-[10px] font-semibold uppercase ${group.tone === 'danger' ? 'text-[#8a4a3a]' : 'text-[#687461]'}`}>{group.label}</p>
              <div className="grid gap-1" onClick={() => setOpen(false)}>
                {group.children}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
